import { Link } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ChartBar, Building2, Clock, Fuel, Boxes, Gauge, Receipt, FileText, Tag, UserSquare2,
  Users, Scale, Headset, ArrowRight, Lock, Copy, ExternalLink, Factory, Database, Weight, Trash2,} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { defaultPeriod } from '../services/defaultPeriod';
import { moduleAccess, reachableModules } from '../components/Shared/moduleAccess';
import { c, t } from '../theme';

const ICONS = {
  mor: ChartBar, mor_own: Building2, overtime: Clock, fuel: Fuel, inventory: Boxes, post_collections: Factory, softpak_reports: Database, scale_pak: Weight, disposal: Trash2, fleet_reports: Database, fleet_dashboard: Gauge,
  sales: Gauge, expense: Receipt, billing: FileText, pricing: Tag, staffing: UserSquare2,
  hr_headcount: Users, time_wage: Scale, contact_center: Headset,
};
const GROUPS = ['Operations', 'Sales & finance', 'People & compliance', 'Contact center'];

// The same five links AppShell carries for everyone.
const EXTERNAL_LINKS = [
  ['Samsara', 'https://cloud.samsara.com/signin'],
  ['Tableau', 'https://dashboard.athensservices.com/#/signin'],
  ['Fleetio', 'https://secure.fleetio.com/login'],
  ['Workday', 'https://wd1-identity.myworkday.com/wday/authgwy/athensservices/upc/login'],
  ['Env. Compliance App', 'https://eac.athensservices.com/'],
];

// The landing page for everyone. "/" comes here unless the person has chosen a
// start page (moduleAccess.js startPath).
//
// What needs attention is built only from MOR today, because MOR is the one
// module with a monthly task attached. It leads with how the yards performed,
// not with how many reviews are in: whether DVPs review is not the point of
// the page, and a count of submissions that may well stay at zero is not
// something to open every day on.
export default function Home() {
  const auth = useAuth();
  const { user, costCenters } = auth;
  const access = moduleAccess(auth);
  const modules = reachableModules(access);

  if (!modules.length) return <NoAccess user={user} logout={auth.logout} />;

  const firstName = (user?.displayName || '').split(' ')[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="hm-page">
      <style>{css}</style>
      <div className="hm-head">
        <div>
          <div className="hm-eyebrow">{today}</div>
          <h1 className="hm-title">{greeting}{firstName ? `, ${firstName}` : ''}</h1>
        </div>
        <StartPagePicker user={user} modules={modules} updateUser={auth.updateUser} />
      </div>

      {access.morCompany && <CompanyAttention />}
      {access.morOwn && <YardAttention costCenters={costCenters} />}

      <section>
        <h2 className="hm-label">Your reports</h2>
        {GROUPS.map((group) => {
          const inGroup = modules.filter((m) => m.group === group);
          if (!inGroup.length) return null;
          return (
            <div key={group} className="hm-group">
              <div className="hm-group-name">{group}</div>
              <div className="hm-grid">
                {inGroup.map((m) => <ReportCard key={m.key} module={m} />)}
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}

function ReportCard({ module: m }) {
  const Icon = ICONS[m.key];
  return (
    <Link to={m.to} className="hm-card">
      <span className="hm-card-icon"><Icon size={17} /></span>
      <span>
        <span className="hm-card-title">{m.label}</span>
        <span className="hm-card-desc">{m.description}</span>
      </span>
    </Link>
  );
}

function StartPagePicker({ user, modules, updateUser }) {
  // A stored choice the person can no longer open reads as Home, which is
  // where "/" sends them in that case (startPath).
  const current = modules.some((m) => m.key === user?.startPage) ? user.startPage : 'home';

  async function choose(e) {
    const value = e.target.value;
    try {
      const saved = await api.put('/auth/me/start-page', { startPage: value === 'home' ? null : value });
      updateUser({ startPage: saved.startPage });
      toast.success(value === 'home' ? 'You will start on Home.' : 'Start page saved.');
    } catch {
      toast.error('Could not save your start page.');
    }
  }

  return (
    <div className="hm-start">
      <label htmlFor="hm-start-page" className="hm-start-label">Open on sign-in</label>
      <select id="hm-start-page" className="hm-select" value={current} onChange={choose}>
        <option value="home">Home</option>
        {modules.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
      </select>
    </div>
  );
}

function usePeriod() {
  const { data: periods, isLoading } = useQuery({ queryKey: ['periods'], queryFn: () => api.get('/periods') });
  return { period: defaultPeriod(periods), isLoading };
}

// Admins and finance: how the month went across every yard.
function CompanyAttention() {
  const { period } = usePeriod();
  const { data } = useQuery({
    queryKey: ['company-dashboard', period?.id],
    queryFn:  () => api.get(`/admin/company-dashboard/${period.id}`),
    enabled:  !!period,
  });
  if (!period || !data) return null;

  const good = num(data.companyRollup.total_good);
  const risk = num(data.companyRollup.total_risk);
  const off  = num(data.companyRollup.total_off);
  const yards = data.costCenters;
  const submitted = yards.filter((y) => y.status === 'submitted' || y.status === 'acknowledged').length;
  const worst = [...yards].filter((y) => num(y.off_count) > 0)
    .sort((a, b) => num(b.off_count) - num(a.off_count)).slice(0, 4);

  return (
    <section>
      <h2 className="hm-label">Needs your attention</h2>
      <div className="hm-attn">
        <div className="hm-attn-body">
          <div className="hm-attn-kicker">{period.label} · Monthly Operations Review</div>
          <div className="hm-attn-title">
            {good + risk + off === 0
              ? 'No scored metrics loaded yet'
              : `${off} metric${off === 1 ? '' : 's'} off track across ${yards.length} yards`}
          </div>
          {good + risk + off > 0 && <HealthBar good={good} risk={risk} off={off} />}
          {worst.length > 0 && (
            <div className="hm-attn-line">
              Most off track: {worst.map((y, i) => (
                <span key={y.cost_center_id}>{i > 0 && ' · '}<b>{y.cost_center_name} {num(y.off_count)}</b></span>
              ))}
            </div>
          )}
          <div className="hm-attn-quiet">
            Reviews: {submitted === 0 ? 'none submitted yet' : `${submitted} of ${yards.length} submitted`}
          </div>
        </div>
        <Link to={`/admin/company/${period.id}`} className="hm-btn">Open MOR Dashboard <ArrowRight size={15} /></Link>
      </div>
    </section>
  );
}

// A DVP: their own yards, most off-track first.
function YardAttention({ costCenters }) {
  const { period } = usePeriod();
  const results = useQueries({
    queries: costCenters.map((cc) => ({
      queryKey: ['review', cc.id, period?.id],
      queryFn:  () => api.get(`/reviews/${cc.id}/${period.id}`),
      enabled:  !!period,
    })),
  });
  if (!period) return null;

  const rows = costCenters.map((cc, i) => {
    const data = results[i]?.data;
    const metrics = data?.sections ? Object.values(data.sections).flat() : [];
    const offMetrics = metrics.filter((m) => m.status === 'off');
    return {
      cc,
      loaded:    !!data,
      status:    data?.review?.status,
      good:      metrics.filter((m) => m.status === 'good').length,
      risk:      metrics.filter((m) => m.status === 'risk').length,
      off:       offMetrics.length,
      explained: offMetrics.filter((m) => m.userComment && m.userComment.trim()).length,
    };
  }).filter((r) => r.loaded).sort((a, b) => b.off - a.off);
  if (!rows.length) return null;

  return (
    <section>
      <h2 className="hm-label">Needs your attention</h2>
      <div className="hm-stack">
        {rows.map((r) => (
          <div key={r.cc.id} className="hm-attn">
            <span className="hm-code">{r.cc.code}</span>
            <div className="hm-attn-body">
              <div className="hm-attn-kicker">{r.cc.name} · {period.label} · {STATUS_WORDS[r.status] || 'no data yet'}</div>
              <div className="hm-attn-title">
                {r.off === 0 ? 'Nothing off track this month' : `${r.off} metric${r.off === 1 ? '' : 's'} off track in ${period.label.split(' ')[0]}`}
              </div>
              {r.good + r.risk + r.off > 0 && <HealthBar good={r.good} risk={r.risk} off={r.off} />}
              {r.off > 0 && r.status !== 'submitted' && r.status !== 'acknowledged' && (
                <div className="hm-attn-quiet">
                  To submit your review, add a comment on each off-track metric ({r.explained} of {r.off} so far).
                </div>
              )}
            </div>
            <Link to={`/review/${r.cc.id}/${period.id}`} className="hm-btn">Open {r.cc.name} review <ArrowRight size={15} /></Link>
          </div>
        ))}
      </div>
    </section>
  );
}

const STATUS_WORDS = {
  pending: 'no data yet', data_loaded: 'review not started', in_progress: 'review in progress',
  submitted: 'review submitted', acknowledged: 'review submitted',
};

function HealthBar({ good, risk, off }) {
  const total = good + risk + off;
  const pct = (n) => `${(n / total) * 100}%`;
  return (
    <>
      <div className="hm-bar" role="img" aria-label={`${good} on target, ${risk} at risk, ${off} off track`}>
        <span style={{ width: pct(good), background: c.success }} />
        <span style={{ width: pct(risk), background: '#EAB308' }} />
        <span style={{ width: pct(off),  background: c.danger }} />
      </div>
      <div className="hm-legend">
        <span><i style={{ background: c.success }} /><b>{good}</b> on target</span>
        <span><i style={{ background: '#EAB308' }} /><b>{risk}</b> at risk</span>
        <span><i style={{ background: c.danger }} /><b>{off}</b> off track</span>
      </div>
    </>
  );
}

// First sign-in, or anyone the portal has nothing to show yet. The old landing
// sent them to an empty MOR review with no word about why.
function NoAccess({ user, logout }) {
  async function copyEmail() {
    try {
      await navigator.clipboard.writeText(user?.email || '');
      toast.success('Email copied.');
    } catch {
      toast.error('Could not copy. Select the address instead.');
    }
  }
  const initial = (user?.displayName || user?.email || '?').charAt(0).toUpperCase();

  return (
    <div className="hm-na">
      <style>{css}</style>
      <span className="hm-na-icon"><Lock size={22} /></span>
      <h1 className="hm-title">Welcome to the Athens Data Portal</h1>
      <p className="hm-na-lead">
        You're signed in, but no reports have been shared with you yet. That's normal for a new
        account: access is added report by report, and until then this page is all you'll see.
      </p>

      <div className="hm-na-card hm-na-who">
        <span className="hm-na-avatar">{initial}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="hm-start-label">Signed in as</div>
          <div className="hm-na-name">{user?.displayName}</div>
          <div className="hm-na-email">{user?.email}</div>
        </div>
        <button type="button" className="hm-btn-secondary" onClick={copyEmail}><Copy size={14} /> Copy email</button>
      </div>
      <div className="hm-na-notyou">
        Not you? <button type="button" className="hm-linkbtn" onClick={logout}>Sign out and use another account</button>
      </div>

      <div className="hm-na-card">
        <h2 className="hm-na-h2">How to get access</h2>
        <ol className="hm-na-steps">
          <li>Contact the IT helpdesk. Include the email above and the reports you need, for example your yard's MOR review, Overtime, or the ASC Dashboard.</li>
          <li>Once access has been added, reload this page. Your reports will appear here and in the sidebar.</li>
        </ol>
        <div><button type="button" className="hm-btn-secondary" onClick={() => window.location.reload()}>Reload</button></div>
      </div>

      <div>
        <h2 className="hm-label">Available to everyone</h2>
        <div className="hm-na-links">
          {EXTERNAL_LINKS.map(([label, href]) => (
            <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="hm-na-link">
              <ExternalLink size={14} /> {label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function num(v) { return parseInt(v, 10) || 0; }

const css = `
.hm-page { max-width: 1200px; margin: 0 auto; display: flex; flex-direction: column; gap: 26px; }
.hm-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; flex-wrap: wrap; }
.hm-eyebrow, .hm-label {
  font-size: ${t.label.fontSize}; font-weight: 700; text-transform: uppercase;
  letter-spacing: ${t.label.letterSpacing}; color: ${c.textMuted};
}
.hm-eyebrow { margin-bottom: 5px; }
.hm-label { margin: 0 0 8px; }
.hm-title { margin: 0; font-size: 24px; font-weight: 650; letter-spacing: -0.02em; color: ${c.text}; }
.hm-start { display: flex; flex-direction: column; gap: 4px; align-items: flex-end; }
.hm-start-label { font-size: 11px; font-weight: 600; color: ${c.textMuted}; }
.hm-select {
  font: inherit; font-size: 13px; padding: 7px 10px; min-width: 200px;
  border: 1px solid ${c.borderStrong}; border-radius: 6px; background: ${c.surface}; color: ${c.text};
}
.hm-stack { display: flex; flex-direction: column; gap: 10px; }
.hm-attn {
  display: flex; gap: 24px; align-items: center; padding: 20px 22px;
  background: ${c.surface}; border: 1px solid ${c.border}; border-radius: 12px;
}
.hm-attn-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 9px; }
.hm-attn-kicker { font-size: 11px; font-weight: 600; color: ${c.textMuted}; }
.hm-attn-title { font-size: 18px; font-weight: 600; color: ${c.text}; }
.hm-attn-line { font-size: 12.5px; color: ${c.textStrong}; }
.hm-attn-quiet { font-size: 12px; color: ${c.textMuted}; }
.hm-code {
  width: 44px; height: 44px; border-radius: 10px; background: #F1F5F9; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: #475569;
}
.hm-bar { display: flex; height: 8px; border-radius: 4px; overflow: hidden; background: ${c.border}; max-width: 560px; }
.hm-legend { display: flex; gap: 18px; flex-wrap: wrap; font-size: 12.5px; color: ${c.textStrong}; }
.hm-legend span { display: flex; align-items: center; gap: 6px; }
.hm-legend i { width: 8px; height: 8px; border-radius: 2px; display: inline-block; }
.hm-btn {
  display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; white-space: nowrap;
  background: ${c.navy}; color: #fff; border-radius: 8px; font-size: 13px; font-weight: 600; text-decoration: none;
}
.hm-btn:hover { background: ${c.navyLight}; }
.hm-group { margin-bottom: 16px; }
.hm-group-name { font-size: 12px; font-weight: 600; color: ${c.textStrong}; margin-bottom: 8px; }
.hm-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 12px; }
.hm-card {
  display: flex; gap: 12px; align-items: flex-start; padding: 16px; text-decoration: none; color: ${c.text};
  background: ${c.surface}; border: 1px solid ${c.border}; border-radius: 12px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.hm-card:hover { border-color: #94A3B8; box-shadow: 0 1px 3px rgba(17,24,39,0.06); }
.hm-card-icon {
  width: 34px; height: 34px; border-radius: 8px; background: #EEF2F8; color: ${c.navy};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.hm-card-title { display: block; font-size: 14px; font-weight: 600; }
.hm-card-desc { display: block; font-size: 12.5px; color: ${c.textMuted}; margin-top: 3px; line-height: 1.45; }

.hm-na { max-width: 620px; margin: 36px auto 0; display: flex; flex-direction: column; gap: 18px; }
.hm-na-icon {
  width: 44px; height: 44px; border-radius: 12px; background: #EEF2F8; color: ${c.navy};
  display: flex; align-items: center; justify-content: center;
}
.hm-na-lead { margin: 0; font-size: 14px; line-height: 1.6; color: ${c.textStrong}; }
.hm-na-card {
  padding: 18px 20px; background: ${c.surface}; border: 1px solid ${c.border}; border-radius: 12px;
  display: flex; flex-direction: column; gap: 12px;
}
.hm-na-who { flex-direction: row; align-items: center; gap: 14px; }
.hm-na-avatar {
  width: 38px; height: 38px; border-radius: 50%; background: ${c.navy}; color: #fff; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 600;
}
.hm-na-name { font-size: 14px; font-weight: 600; }
.hm-na-email { font-size: 13px; color: ${c.textStrong}; overflow-wrap: anywhere; }
.hm-na-notyou { font-size: 13px; color: ${c.textStrong}; margin-top: -8px; }
.hm-na-h2 { margin: 0; font-size: 15px; font-weight: 600; }
.hm-na-steps { margin: 0; padding-left: 20px; display: flex; flex-direction: column; gap: 8px; font-size: 13.5px; line-height: 1.55; color: ${c.textStrong}; }
.hm-na-links { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 8px; }
.hm-na-link {
  display: flex; align-items: center; gap: 8px; padding: 10px 12px; text-decoration: none; color: ${c.text};
  background: ${c.surface}; border: 1px solid ${c.border}; border-radius: 8px; font-size: 13px; font-weight: 500;
}
.hm-btn-secondary {
  display: inline-flex; align-items: center; gap: 6px; font: inherit; font-size: 13px; font-weight: 500;
  padding: 8px 12px; border: 1px solid ${c.borderStrong}; border-radius: 8px; background: ${c.surface};
  color: ${c.text}; cursor: pointer;
}
.hm-linkbtn {
  font: inherit; font-size: 13px; color: ${c.info}; background: none; border: none; padding: 0;
  cursor: pointer; text-decoration: underline;
}
@media (max-width: 720px) {
  .hm-attn { flex-direction: column; align-items: stretch; }
  .hm-start { align-items: stretch; width: 100%; }
}
`;
